// English is the canonical source of truth AND the fallback.
// Keep this file complete. Every key here must exist here.

const en = {
  nav: {
    howItWorks: "How it works",
    features: "Features",
    updates: "Updates",
    useSpotDrop: "Use SpotDrop",
    langLabel: { en: "English", mt: "Maltese (Malti)", it: "Italian (Italiano)" },
  },
  hero: {
    badge: "Live in Malta",
    headline: "Find parking before it disappears.",
    sub: "Live parking spots, dropped and claimed by drivers across Malta. Drop your spot when you leave, find one when you don't.",
    ctaPrimary: "Use SpotDrop",
    ctaSecondary: "How it works",
    ctaPlayAlt: "Get it on Google Play",
    ctaPlayNote: "Android app · free",
    ctaInstallWeb: "Install Web App",
    trust: { free: "Free to use", malta: "Built for Malta", nosignup: "No sign-up needed" },
  },
  how: {
    eyebrow: "How it works",
    title: "Three simple steps for every driver in Malta.",
    subtitle: "Whether you're leaving or looking.",
    card1: { tag: "01 · Drop", title: "Leaving a spot?", desc: "Drop your parking spot before you leave so the next driver can claim it instantly." },
    card2: { tag: "02 · Find", title: "Looking for parking?", desc: "See parking spots drop on the live map around you the second drivers open a bay." },
    card3: { tag: "03 · Handover", title: "Meeting the next driver?", desc: "Choose \"I can wait\" or \"I can't wait\" so the arriving driver knows what to expect — smooth, honest handovers." },
  },
  features: {
    eyebrow: "Features",
    title: "Built like a Malta-native parking grid.",
    tag: "Designed for everyday drivers",
    card1: { title: "Real-time live map", desc: "See spots pop up on the map the moment drivers open a bay near you." },
    card2: { title: "Smart location system", desc: "Pinpoint accuracy tuned for Malta's narrow streets." },
    card3: { title: "Blue Badge, Loading & Moto bays", desc: "Drop and find Normal, Blue Badge, Loading and Motorcycle spots — respect the local rules." },
    card4: { title: "Remember where you parked", desc: "Save your parking location and open walking directions back to your car." },
    card5: { title: "Fast and simple", desc: "Two taps to drop. Two taps to claim. That's it." },
    card6: { title: "Community driven", desc: "Every driver in Malta makes the network smarter." },
  },
  showcase: {
    eyebrow: "Inside the app",
    title: "A peek at SpotDrop.",
    sub: "Three screens. One mission — never circle for parking again.",
    ctaCopy: "SpotDrop is live in Malta. Open it and try it.",
    ctaBtn: "Use SpotDrop",
    liveNow: "Live now",
    liveTitle: "Features you can use today",
    live1: { title: "Parking Assistant", desc: "Tell it where you're going — it finds the best parking nearby and walks you there.", cta: "Find parking" },
    live2: { title: "Save My Car", desc: "Save where you parked and get walking directions straight back to it.", cta: "Save my car" },
    live3: { title: "Community spots", desc: "Drivers drop their spot as they leave, so you can claim one instead of circling.", cta: "See live spots" },
    mockup1: { tag: "01 · Live map", title: "Real-time parking", liveSpots: "Live spots", leavingIn5: "Leaving in 5 min", leavingIn10: "Leaving in 10 min" },
    mockup2: {
      tag: "02 · Drop", title: "Leaving a spot", leavingLabel: "Leaving in",
      min5: "5 min", min10: "10 min", min15: "15 min",
      hint: "Nearby drivers will see it on the map", dropBtn: "Drop the spot",
      doneTitle: "Spot dropped", doneSub: "Nearby drivers can see your spot on the map.",
    },
    mockup3: {
      tag: "03 · Handover", title: "I can wait / I can't wait",
      headerTitle: "Handover", arrivingLabel: "Arriving driver",
      arrivingName: "Luca · White Fiat 500", arrivingMeta: "Plate JFK-224 · 120m away",
      prompt: "Can you wait?", subprompt: "Tell them what to expect",
      canWait: "I can wait", canWaitDesc: "I'll stay until Luca arrives.",
      cantWait: "I can't wait", cantWaitDesc: "Leaving now — first driver takes it.",
      footer: "Honest handovers keep the map fair for everyone.",
    },
  },
  roadmap: {
    eyebrow: "Roadmap",
    title: "What we're building next.",
    sub: "A glimpse at the things we're working on. Shipped one street at a time.",
    chipLive: "Live",
    chipSoon: "Coming soon",
    footnote: "Roadmap items, not a commitment. SpotDrop is an information and community platform — public parking is never reserved or guaranteed.",
    item1: { title: "Drop a parking spot", desc: "Share your spot the moment you leave." },
    item2: { title: "Claim a spot", desc: "Claim an open spot on the live map before someone else gets there." },
    item3: { title: "Private parking rentals", desc: "Monthly bays from verified owners." },
    item4: { title: "Save my car location", desc: "Never lose where you parked again." },
    item5: { title: "Favourite areas", desc: "Smart alerts for the streets you care about." },
    item6: { title: "Community points & reputation", desc: "Reward active, trusted contributors." },
    item7: { title: "Anti-spam & anti-bot protection", desc: "Real drivers, real spots." },
    item8: { title: "Event / Beach / Festival Mode", desc: "Spot the rush for feasts, festivals and beach days." },
    item9: { title: "Support SpotDrop", desc: "Free for the community. Optional support to help us improve." },
    item10: { title: "Supporter plan (future)", desc: "Optional perks to back the project." },
  },
  updates: {
    eyebrow: "Updates",
    title: "Get updates",
    sub: "SpotDrop is live and free to use. Drop your email if you want occasional updates — new features, coverage updates, and important SpotDrop news.",
    field: {
      name: "Your name", namePlaceholder: "Alex",
      email: "Email", emailPlaceholder: "[email protected]",
      area: "Area in Malta", areaPlaceholder: "Sliema, St Julian's, Valletta…",
    },
    submit: "Get updates",
    submitting: "Subscribing...",
    successTitle: "You're on the updates list.",
    successBody: "We saved your email. We'll only send occasional SpotDrop news.",
    alreadyOn: "You're already on the updates list.",
    alreadyBody: "This email is already saved. We'll only send occasional SpotDrop news.",
    launchNote: "Live in Malta · Free to use",
  },
  footer: {
    tagline: "The community-powered parking network for Malta.",
    updates: "Get updates",
    terms: "Terms",
    privacy: "Privacy",
    disclaimer: "Disclaimer",
    instagramAlt: "Follow SpotDrop on Instagram",
    copyright: "© {year} SpotDrop Malta. All rights reserved.",
  },
  legal: {
    terms: {
      title: "Terms of Use",
      intro: "These terms govern your use of SpotDrop. Please read them before using the service.",
      notice: "This document is currently maintained in English. A translated version is coming soon.",
    },
    privacy: {
      title: "Privacy Policy",
      intro: "We take your privacy seriously. Here's how SpotDrop handles your data.",
      notice: "This document is currently maintained in English. A translated version is coming soon.",
    },
    disclaimer: {
      title: "Disclaimer",
      intro: "SpotDrop is an information and community platform. Public parking is never reserved or guaranteed.",
      notice: "This document is currently maintained in English. A translated version is coming soon.",
    },
  },
  common: { close: "Close", cancel: "Cancel", back: "Back", save: "Save", language: "Language", active: "Active" },
};

export default en;
